import { useEffect, useMemo, useState } from 'react';
import { useAppAuth } from '../components/AuthContext.jsx';
import { usePolicy } from '../components/PolicyContext.jsx';
import {
  listUsers,
  createUser,
  updateUser,
  ROLES,
  STATUSES,
  ROLE_LABEL,
  STATUS_LABEL,
} from '../services/users.js';

// Accounts & groups (JCH — Účty a skupiny). Two modes:
//  1. List everyone grouped by role, with a live status chip and a role/status
//     quick-edit inline on each row (mirrors the JCH "Účty a skupiny" mockup).
//  2. A "Nový účet" form that creates an account (assigns role + status).
//
// The backend is JCH-only: any caller without the JCH role gets a 403. We never
// return or store password_hash.
const EMPTY_FORM = {
  username: '',
  email: '',
  emailer: '',
  password: '',
  fullName: '',
  position: '',
  role: 'Makler',
  status: 'active',
  cohort: '',
  examDate: '',
  levelName: 'Rozjezd',
};

const fmtDate = (v) => new Date(v).toLocaleDateString('cs-CZ');

export default function Accounts() {
  const { policy } = usePolicy();
  const levelOptions = policy.levels.slice(1).map((l) => [l.name, l.numeric]);
  const { user: me } = useAppAuth();
  const isJch = me?.role === 'JCH';
  const [groups, setGroups] = useState({ JCH: [], JR: [], Makler: [] });
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM, levelName: policy.levels[1].name });
  const [submitError, setSubmitError] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteSent, setInviteSent] = useState(true);
  const [drafts, setDrafts] = useState({}); // { [id]: { role|status } } for inline edits

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { groups: g, count: c } = await listUsers();
      setGroups(g);
      setCount(c);
    } catch (err) {
      setError(err.message || 'Účty se nepodařilo načíst');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const activeCount = useMemo(
    () =>
      Object.values(groups).flat().filter((u) => u.status === 'active').length,
    [groups]
  );

  const updateForm = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');
    setSubmitting(true);
    try {
      const created = await createUser({
        ...form,
        levelNumeric: levelOptions.find(([n]) => n === form.levelName)?.[1],
        email: form.email || form.emailer,
      });
      setGroups((prev) => ({
        ...prev,
        [created.role]: [...(prev[created.role] || []), created],
      }));
      setCount((n) => n + 1);
      setForm({ ...EMPTY_FORM, levelName: policy.levels[1].name });
      setInviteUrl(created.invite?.url || '');
      setInviteSent(created.invite?.emailSent !== false);
    } catch (err) {
      setSubmitError(err.message || 'Účet se nepodařilo vytvořit');
    } finally {
      setSubmitting(false);
    }
  };

  // Inline role/status edits for one account. Saves automatically when a value
  // changes and the row is still loading; the edit is saved automatically.
  const patch = async (id, field, value) => {
    const next = { ...(drafts[id] || {}), [field]: value };
    if (field === 'emailer' && !next.email) next.email = value;
    setDrafts((prev) => ({ ...prev, [id]: next }));
    try {
      const updated = await updateUser(id, next);
      setGroups((prev) => {
        const byRole = { ...prev };
        for (const role of ROLES) {
          byRole[role] = (byRole[role] || []).map((u) =>
            u.id === id ? { ...u, ...next, email: updated.email || next.email || u.email } : u
          );
        }
        return byRole;
      });
      setDrafts((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    } catch (err) {
      setError(err.message || 'Účet se nepodařilo aktualizovat');
    }
  };

  const chipClass = (status) => `accounts__chip accounts__chip--${status}`;

  return (
    <section className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">Účty a skupiny</h1>
          <p className="dashboard__lead">
            Makléře připravuješ ty: údaje, skupinu a termíny. Drill mu pošle pozvánku a makléř si účet
            dokončí sám odkazem na firemní e-mail. Datum nástupu je začátek kurzu RX Akademie;
            počítají se z něj měsíce cesty, bránové schůzky a udržení.
          </p>
        </div>
        <div className="accounts__stats">
          <span className="accounts__stat">
            Aktivní: <strong>{activeCount}</strong>
          </span>
          <span className="accounts__stat">Celkem: <strong>{count}</strong></span>
        </div>
      </div>

      {error && (
        <p className="dashboard__error" role="alert">
          {error}
        </p>
      )}

      {/* Create account */}
      <article className="card">
        <h2 className="card__title">Připravit makléře a poslat pozvánku</h2>
        <form className="auth__form" onSubmit={handleSubmit}>
          {form.role !== 'Makler' && <label className="auth__label">
            Uživatelské jméno
            <input
              className="auth__input"
              value={form.username}
              onChange={updateForm('username')}
              placeholder="jnovak"
            />
          </label>}

          <label className="auth__label">
            Firemní e-mail / e-mailer
            <input
              className="auth__input"
              type="email"
              required
              value={form.email || form.emailer}
              onChange={updateForm('emailer')}
              placeholder="jnovak@partner.cz"
            />
          </label>

          {form.role !== 'Makler' && (
            <label className="auth__label">
              Heslo
              <input
                className="auth__input"
                type="password"
                value={form.password}
                onChange={updateForm('password')}
                placeholder="Min. 8 znaků"
              />
            </label>
          )}

          <label className="auth__label">
            Jméno a příjmení
            <input
              className="auth__input"
              value={form.fullName}
              onChange={updateForm('fullName')}
              placeholder="Jan Novák"
            />
          </label>

          <div className="auth__row">
            <label className="auth__label">
              Role
              <select className="auth__input" value={form.role} onChange={updateForm('role')}>
                {ROLES.filter((r) => isJch || r === 'Makler').map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </label>

            <label className="auth__label">
              Stav
              <select className="auth__input" value={form.status} onChange={updateForm('status')}>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {form.role === 'Makler' && (
            <div className="auth__row">
              <label className="auth__label">
                Skupina – začátek kurzu RX Akademie
                <input
                  className="auth__input"
                  type="month"
                  value={form.cohort}
                  onChange={updateForm('cohort')}
                />
              </label>
              <label className="auth__label">
                Termín zkoušky (lze doplnit později)
                <input
                  className="auth__input"
                  type="date"
                  value={form.examDate}
                  onChange={updateForm('examDate')}
                />
              </label>
              <label className="auth__label">
                Úroveň
                <select className="auth__input" value={form.levelName} onChange={updateForm('levelName')}>
                  {levelOptions.map(([name, n]) => (
                    <option key={name} value={name}>{name} (úroveň {n})</option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {inviteUrl && (
            <p className="dashboard__status" role="status">
              {inviteSent ? 'Pozvánka odeslána.' : 'E-mail se nepodařilo odeslat – pošli odkaz ručně.'} Odkaz: <a href={inviteUrl}>{inviteUrl}</a>
            </p>
          )}

          {submitError && (
            <p className="dashboard__error" role="alert">
              {submitError}
            </p>
          )}

          <button className="button button--spot" type="submit" disabled={submitting}>
            {submitting ? 'Vytvářím…' : 'Vytvořit účet'}
          </button>
        </form>
      </article>

      {/* Roster grouped by role, per mockup */}
      {loading ? (
        <p className="dashboard__status">Načítá se…</p>
      ) : (
        Object.entries(groups).map(([role, users]) => (
          <article key={role} className="card">
            <h2 className="card__title">
              {ROLE_LABEL[role]} · {users.length}
            </h2>
            {users.length === 0 ? (
              <p className="dashboard__status">Zatím nikdo.</p>
            ) : (
              <ul className="accounts__list">
                {users.map((u) => (
                  <li key={u.id} className="accounts__item">
                    <div className="accounts__info">
                      <span className="accounts__name">{u.full_name || u.username}</span>
                      <span className="accounts__email">{u.email}</span>
                      {u.position && <span className="accounts__position">{u.position}</span>}
                      {u.role === 'Makler' && (
                        <span className="accounts__position">
                          Přístup: {u.status === 'inactive'
                            ? 'čeká na přijetí pozvánky v e-mailu'
                            : STATUS_LABEL[u.status]} ·{' '}
                          Podmínky a údaje:{' '}
                          {u.consent_accepted_at
                            ? `odsouhlaseno ${fmtDate(u.consent_accepted_at)} · vzato na vědomí ${fmtDate(u.consent_privacy_accepted_at || u.consent_accepted_at)}`
                            : 'čeká na odsouhlasení'}
                        </span>
                      )}
                    </div>

                    <div className="accounts__controls">
                      <label className="accounts__field">
                        Role
                        <select
                          className="auth__input auth__input--compact"
                          value={drafts[u.id]?.role ?? u.role}
                          onChange={(e) => patch(u.id, 'role', e.target.value)}
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABEL[r]}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="accounts__field">
                        Stav
                        <select
                          className="auth__input auth__input--compact"
                          value={drafts[u.id]?.status ?? u.status}
                          onChange={(e) => patch(u.id, 'status', e.target.value)}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABEL[s]}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="accounts__field">
                        E-mail
                        <input
                          className="auth__input auth__input--compact"
                          type="email"
                          value={drafts[u.id]?.email ?? u.email ?? ''}
                          onChange={(e) => patch(u.id, 'emailer', e.target.value)}
                        />
                      </label>
                    </div>

                    <span className={chipClass(u.status)}>
                      {STATUS_LABEL[u.status] || u.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))
      )}
    
      <article className="card">
        <h2 className="card__title">Kdo co smí</h2>
        <ul className="jr-list">
          <li><strong>Makléř</strong> – vidí a zapisuje jen svoje data. Cizí čísla v Drillu nevidí.</li>
          <li><strong>Manažerský přístup</strong> – JR a JCH, případně další člověk jako zástup: vidí vše, připravuje a ruší účty, mění nastavení, plán makléře i zápis úrovně.</li>
          <li><strong>JCH navíc</strong> – potvrzuje svůj milníkový hovor a při neshodě o úrovni rozhoduje.</li>
        </ul>
        <p className="card__hint">
          Ukončení místo mazání: makléř ztratí přístup a zmizí z přehledů, jeho čísla zůstanou v
          udržení. Stav změníš v řádku výše. Data zůstávají 3 roky od odchodu.
        </p>
      </article>
    </section>
  );
}
